import { useState } from "react";
import { X, Plus, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { API, formatApiError } from "@/lib/api";

const TYPES = [
  { v: "rpg", l: "Tabletop RPG" },
  { v: "novel", l: "Novel" },
  { v: "screenplay", l: "Screenplay" },
  { v: "video_game", l: "Video Game" },
  { v: "comic", l: "Comic" },
  { v: "other", l: "Other" },
];

const STAT_KEYS = ["str", "dex", "con", "int", "wis", "cha"];

const DEFAULT = {
  name: "",
  hook: "",
  tags: [],
  character_type: "rpg",
  aliases: [],
  personality: "",
  preview_backstory: "",
  full_backstory: "",
  gallery_paths: [],
  has_rpg_build: true,
  rpg_build: {
    system: "D&D 5e", character_class: "", race: "", level: 1, hp: 10, ac: 10,
    stats: { str: 10, dex: 10, con: 10, int: 10, wis: 10, cha: 10 },
    gear: [], spells: [], abilities: [],
  },
  freeform_build: "",
  price_cents: 500,
  price_dollars: "5.00",
  license_tier: "standard",
};

const inputCls = "w-full bg-[#0D0C0A] border border-[rgba(217,180,114,0.15)] focus:border-[#D9B472] outline-none rounded-sm px-4 py-2.5 text-[#F7F3EC] font-mono-brass text-sm";

function Field({ label, children, hint }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass block mb-1.5">{label}</label>
      {children}
      {hint && <p className="text-[10px] text-[#6E675E] font-serif-body italic mt-1">{hint}</p>}
    </div>
  );
}

function TokenInput({ tokens, onChange, placeholder, testid, max = 12 }) {
  const [text, setText] = useState("");
  const add = () => {
    const t = text.trim();
    if (!t) return;
    if (tokens.includes(t)) { setText(""); return; }
    if (tokens.length >= max) { toast.error(`Max ${max}`); return; }
    onChange([...tokens, t]);
    setText("");
  };
  return (
    <div>
      <div className="flex gap-2">
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          className={inputCls}
          data-testid={testid}
        />
        <button type="button" onClick={add} className="btn-ghost-gold px-3 rounded-sm font-mono-brass text-[10px] uppercase tracking-widest">Add</button>
      </div>
      {tokens.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {tokens.map((t, i) => (
            <span key={i} className="inline-flex items-center gap-1 text-[10px] font-mono-brass px-2 py-1 border border-[rgba(217,180,114,0.25)] text-[#D9B472] rounded-sm">
              {t}
              <button type="button" onClick={() => onChange(tokens.filter((_, j) => j !== i))} className="hover:text-[#F7F3EC]"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function GalleryUploader({ paths, onChange, max = 5 }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    if (paths.length >= max) { toast.error(`Max ${max} images`); return; }
    if (!file.type.startsWith("image/")) { toast.error("Choose an image"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Under 5 MB"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/upload/cover", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange([...paths, data.path]);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };
  return (
    <div>
      <div className="flex flex-wrap gap-3">
        {paths.map((p, i) => (
          <div key={p} className="relative w-24 h-32 rounded-sm overflow-hidden border border-[rgba(217,180,114,0.2)]" data-testid={`gallery-item-${i}`}>
            <img src={`${API}/files/${p}`} alt="" className="w-full h-full object-cover" />
            <button type="button" onClick={() => onChange(paths.filter((_, j) => j !== i))} className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center bg-black/70 text-[#F7F3EC] rounded-sm">
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        {paths.length < max && (
          <label className="flex flex-col items-center justify-center w-24 h-32 border border-dashed border-[rgba(217,180,114,0.3)] rounded-sm cursor-pointer hover:border-[#D9B472] transition-colors duration-200" data-testid="gallery-add">
            <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
            <Upload className="w-4 h-4 text-[#D9B472] mb-1" />
            <span className="text-[10px] text-[#A8A095] font-mono-brass uppercase tracking-widest">{uploading ? "Up…" : "Add"}</span>
          </label>
        )}
      </div>
      <p className="text-[10px] text-[#6E675E] font-serif-body italic mt-2">Up to {max} images. First is the portrait.</p>
    </div>
  );
}

export default function CharacterModal({ onClose, onCreated }) {
  const [form, setForm] = useState(DEFAULT);
  const [saving, setSaving] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const updRpg = (k) => (e) => setForm({ ...form, rpg_build: { ...form.rpg_build, [k]: e.target.value } });
  const updStat = (k) => (e) => setForm({ ...form, rpg_build: { ...form.rpg_build, stats: { ...form.rpg_build.stats, [k]: parseInt(e.target.value || 0, 10) } } });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        hook: form.hook,
        tags: form.tags,
        character_type: form.character_type,
        aliases: form.aliases,
        personality: form.personality,
        preview_backstory: form.preview_backstory,
        full_backstory: form.full_backstory,
        gallery_paths: form.gallery_paths,
        has_rpg_build: form.has_rpg_build,
        rpg_build: form.has_rpg_build ? {
          system: form.rpg_build.system || null,
          character_class: form.rpg_build.character_class || null,
          race: form.rpg_build.race || null,
          level: parseInt(form.rpg_build.level, 10) || null,
          hp: parseInt(form.rpg_build.hp, 10) || null,
          ac: parseInt(form.rpg_build.ac, 10) || null,
          stats: form.rpg_build.stats,
          gear: form.rpg_build.gear,
          spells: form.rpg_build.spells,
          abilities: form.rpg_build.abilities,
        } : null,
        freeform_build: form.freeform_build || null,
        price_cents: Math.round(parseFloat(form.price_dollars) * 100),
        license_tier: form.license_tier,
      };

      const resp = await api.post("/characters", payload);
      if (resp.status === 202 && resp.data.job_id) {
        toast.info("Long dossier — running content review. This can take up to a minute.", { duration: 6000 });
        const jobId = resp.data.job_id;
        const started = Date.now();
        while (Date.now() - started < 120000) {
          await new Promise((r) => setTimeout(r, 2500));
          try {
            const { data } = await api.get(`/characters/moderation-jobs/${jobId}`);
            if (data.status === "approved") { toast.success("Character listed for sale."); onCreated(); return; }
            if (data.status === "rejected") { toast.error(`Rejected: ${data.reason || "policy violation"}`); setSaving(false); return; }
            if (data.status === "error") { toast.error("Content review failed."); setSaving(false); return; }
          } catch (err) { console.error("Job poll failed", err); }
        }
        toast.error("Review is taking longer than expected — check your dashboard shortly.");
        onCreated();
        return;
      }
      toast.success("Character listed for sale.");
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto" onClick={onClose} data-testid="character-modal">
      <div className="relative w-full max-w-3xl bg-[#161412] border border-[rgba(217,180,114,0.25)] rounded-sm p-6 sm:p-8 my-8" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-[#6E675E] hover:text-[#F7F3EC]" data-testid="character-close"><X className="w-5 h-5" /></button>
        <div className="chip mb-3">List a character</div>
        <h3 className="font-serif-display text-3xl text-[#F7F3EC] mb-2">Sell a forgotten hero or villain</h3>
        <p className="font-serif-body italic text-[#A8A095] mb-6">Give them a name and a portrait. Give them a build if they'll be rolled. Give them a story worth taking home.</p>

        <form onSubmit={submit} className="space-y-5">
          <Field label="Portrait & gallery"><GalleryUploader paths={form.gallery_paths} onChange={(v) => setForm({ ...form, gallery_paths: v })} /></Field>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Character name"><input required maxLength={120} value={form.name} onChange={upd("name")} className={inputCls} data-testid="character-name" /></Field>
            <Field label="Character type">
              <select value={form.character_type} onChange={upd("character_type")} className={inputCls} data-testid="character-type">
                {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
            </Field>
          </div>

          <Field label="One-line hook (public)" hint="A single sentence that makes a buyer stop scrolling.">
            <input required minLength={5} maxLength={280} value={form.hook} onChange={upd("hook")} className={inputCls} placeholder="A gutter-born saint who forgot her own name and remembers everyone else's." data-testid="character-hook" />
          </Field>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Tags"><TokenInput tokens={form.tags} onChange={(v) => setForm({ ...form, tags: v })} placeholder="e.g. Rogue L12, Grimdark, Homebrew" testid="character-tag-input" /></Field>
            <Field label="Known aliases"><TokenInput tokens={form.aliases} onChange={(v) => setForm({ ...form, aliases: v })} placeholder="Add an alias & press Enter" testid="character-alias-input" max={8} /></Field>
          </div>

          <Field label="Personality (public)">
            <textarea required rows={3} maxLength={1000} value={form.personality} onChange={upd("personality")} className={`${inputCls} font-serif-body`} data-testid="character-personality" />
          </Field>

          <Field label="Preview backstory (public teaser)" hint="Free excerpt. Buyers see this before paying.">
            <textarea required rows={4} maxLength={4000} value={form.preview_backstory} onChange={upd("preview_backstory")} className={`${inputCls} font-serif-body`} data-testid="character-preview" />
          </Field>

          <Field label="Full backstory (locked)" hint="The real dossier. Only buyers unlock this.">
            <textarea required rows={6} value={form.full_backstory} onChange={upd("full_backstory")} className={`${inputCls} font-serif-body`} data-testid="character-full" />
          </Field>

          {/* Build toggle */}
          <div className="border border-[rgba(217,180,114,0.15)] rounded-sm p-4 bg-[rgba(217,180,114,0.03)]">
            <label className="flex items-center gap-3 mb-3 cursor-pointer">
              <input type="checkbox" checked={form.has_rpg_build} onChange={(e) => setForm({ ...form, has_rpg_build: e.target.checked })} className="accent-[#D9B472]" data-testid="character-has-rpg-build" />
              <span className="font-mono-brass text-xs uppercase tracking-widest text-[#D9B472]">Structured RPG stat block</span>
            </label>

            {form.has_rpg_build && (
              <div className="space-y-3" data-testid="character-rpg-block">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <Field label="System"><input value={form.rpg_build.system || ""} onChange={updRpg("system")} className={inputCls} placeholder="D&D 5e" data-testid="rpg-system" /></Field>
                  <Field label="Class"><input value={form.rpg_build.character_class || ""} onChange={updRpg("character_class")} className={inputCls} placeholder="Rogue / Arcane Trickster" data-testid="rpg-class" /></Field>
                  <Field label="Race / origin"><input value={form.rpg_build.race || ""} onChange={updRpg("race")} className={inputCls} placeholder="Half-elf" data-testid="rpg-race" /></Field>
                  <Field label="Level"><input type="number" min={0} max={99} value={form.rpg_build.level ?? ""} onChange={updRpg("level")} className={inputCls} data-testid="rpg-level" /></Field>
                  <Field label="HP"><input type="number" min={0} value={form.rpg_build.hp ?? ""} onChange={updRpg("hp")} className={inputCls} data-testid="rpg-hp" /></Field>
                  <Field label="AC"><input type="number" min={0} max={99} value={form.rpg_build.ac ?? ""} onChange={updRpg("ac")} className={inputCls} data-testid="rpg-ac" /></Field>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass mb-2">Ability scores</div>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                    {STAT_KEYS.map((k) => (
                      <div key={k}>
                        <div className="text-[10px] uppercase text-[#D9B472] font-mono-brass mb-1 text-center">{k}</div>
                        <input type="number" min={0} max={30} value={form.rpg_build.stats[k]} onChange={updStat(k)} className={`${inputCls} text-center`} data-testid={`rpg-stat-${k}`} />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="grid sm:grid-cols-3 gap-3">
                  <Field label="Gear"><TokenInput tokens={form.rpg_build.gear} onChange={(v) => setForm({ ...form, rpg_build: { ...form.rpg_build, gear: v } })} placeholder="+2 Longsword" testid="rpg-gear" max={30} /></Field>
                  <Field label="Spells"><TokenInput tokens={form.rpg_build.spells} onChange={(v) => setForm({ ...form, rpg_build: { ...form.rpg_build, spells: v } })} placeholder="Misty Step" testid="rpg-spells" max={40} /></Field>
                  <Field label="Abilities / feats"><TokenInput tokens={form.rpg_build.abilities} onChange={(v) => setForm({ ...form, rpg_build: { ...form.rpg_build, abilities: v } })} placeholder="Sneak Attack" testid="rpg-abilities" max={20} /></Field>
                </div>
              </div>
            )}
          </div>

          <Field label={form.has_rpg_build ? "Free-form build notes (optional)" : "Free-form build (markdown supported)"} hint="Use this if the character has non-RPG stats — a novel character sheet, a video-game loadout, etc.">
            <textarea rows={5} maxLength={8000} value={form.freeform_build} onChange={upd("freeform_build")} className={`${inputCls} font-serif-body`} data-testid="character-freeform" />
          </Field>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Price (USD)"><input required type="number" min={1} step="0.01" value={form.price_dollars} onChange={upd("price_dollars")} className={inputCls} data-testid="character-price" /></Field>
            <Field label="Licence" hint="Roleplay licence lets buyers use them in their own campaign or novel.">
              <select value={form.license_tier} onChange={upd("license_tier")} className={inputCls} data-testid="character-license">
                <option value="standard">Standard — read & keep</option>
                <option value="roleplay">Roleplay licence — read & use</option>
              </select>
            </Field>
          </div>

          <button type="submit" disabled={saving} className="btn-buy w-full rounded-sm py-3.5 font-mono-brass text-xs uppercase tracking-widest font-semibold" data-testid="character-submit">
            {saving ? "Listing…" : "List character anonymously"}
          </button>
        </form>
      </div>
    </div>
  );
}
